import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownState,
  DropdownToggle
} from '@vmware-react/clarity/dist/dropdown';
import cn from 'classnames';
import React, { ReactElement, useState } from 'react';
import './Breadcrumb.css';

interface Props {
  visibleUnits?: number;
}
interface UnitProps {
  children?: React.ReactNode;
  last?: boolean;
  onClick?: () => void;
}

export const Breadcrumb: React.FC<Props> = props => {
  const { children, visibleUnits = 6 } = props;
  const breadcrumbUnits: Array<ReactElement<UnitProps>> = [];

  const [dropdownState, setDropdownState] = useState<DropdownState>();

  const onToggleDropdown = (s: DropdownState) =>
    setDropdownState(s === 'closed' ? 'open' : 'closed');

  const onDismissDropdown = () => {
    setDropdownState('closed');
  };

  React.Children.forEach(children, child => {
    if (React.isValidElement<UnitProps>(child)) {
      breadcrumbUnits.push(child);
    }
  });

  const renderDropdownUnits = () => {
    const numberOfUnits = breadcrumbUnits.length;
    const dropdownUnits = breadcrumbUnits.slice(
      0,
      numberOfUnits - visibleUnits
    );
    return numberOfUnits > visibleUnits ? (
      <Dropdown
        state={dropdownState}
        onToggleClick={onToggleDropdown}
        onDismissDropdown={onDismissDropdown}>
        <DropdownToggle
          icon={{
            shape: 'ellipsis-horizontal',
            className: 'crc-breadcrumb-dropdown'
          }}
        />
        <DropdownMenu>
          {React.Children.map(
            dropdownUnits,
            (child: ReactElement<UnitProps>) => {
              const item = child.props.children;
              return <DropdownItem>{item}</DropdownItem>;
            }
          )}
        </DropdownMenu>
      </Dropdown>
    ) : null;
  };

  const renderVisibleUnits = () => {
    const numberOfUnits = breadcrumbUnits.length;
    const units = breadcrumbUnits.slice(
      numberOfUnits - visibleUnits < 0 ? 0 : numberOfUnits - visibleUnits,
      numberOfUnits
    );
    const numberOfVisibleUnits = units.length;
    return React.Children.map(
      units,
      (child: ReactElement<UnitProps>, index) => {
        if (index !== numberOfVisibleUnits - 1) {
          return child;
        } else {
          return React.cloneElement(child, {
            last: true
          });
        }
      }
    );
  };

  return (
    <div role="region">
      <div role="list" className="crc-breadcrumb">
        {renderDropdownUnits()}
        {renderVisibleUnits()}
      </div>
    </div>
  );
};

export const BreadcrumbUnit: React.FC<UnitProps> = props => {
  const { children, onClick, last } = props;

  return (
    <div className="crc-breadcrumb-unit">
      <button
        disabled={last}
        role="listitem"
        className={cn('crc-breadcrumb-unit-text', { 'last-unit': last })}
        onClick={onClick}>
        {children}
      </button>
      {!last ? (
        <span className="crc-breadcrumb-unit-delimiter">
          <clr-icon shape="caret right" size="12" />
        </span>
      ) : null}
    </div>
  );
};
